import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import { HEADING_FONT } from "../theme/fonts";
import { useToast } from "./ToastProvider";
import api from "../api";
import ServerConnectionForm, {
  connFromServer,
  connToServerPayload,
} from "./ServerConnectionForm";
import { useDeployInfo, useDeployZip, useDeployFromRepo, useGithubStatus } from "../hooks/queries";

const BLUE = "#3B82F6";
const PURPLE = "#7C3AED";
const MUTED = "#64748B";
const HAIRLINE = "#E2E8F0";
const TILE = "#F8FAFC";

// Confirm-and-deploy dialog: shows exactly which server and folder the ZIP
// will land in, because a deploy overwrites a live website.
function DeployDialog({ domain, open, onClose }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const info = useDeployInfo(domain?.id, open);
  const deploy = useDeployZip();
  const githubStatus = useGithubStatus().data;

  const deployRepo = useDeployFromRepo();
  const [zipFile, setZipFile] = useState(null);
  const [path, setPath] = useState("");
  const [progress, setProgress] = useState("");
  // "repo" = take the code already in the domain's GitHub repo (one click),
  // "zip" = upload an archive from this computer.
  const [source, setSource] = useState("repo");
  // Connection details can be filled in right here when the server has none,
  // instead of sending people to the Servers page mid-deploy. Saved onto the
  // server record (encrypted there) before the deploy runs.
  const [conn, setConn] = useState(null);
  // Tracks whether the connection form was touched, so a saved server is only
  // written to when something actually changed.
  const [connEdited, setConnEdited] = useState(false);
  const markEdited = (updater) => {
    setConnEdited(true);
    setConn(updater);
  };
  const [savingConn, setSavingConn] = useState(false);
  // A deploy handed to GitHub Actions: { requestId, state, conclusion, url }.
  // It runs on GitHub's side, so the dialog may close while it does.
  const [actionsRun, setActionsRun] = useState(null);
  const [starting, setStarting] = useState(false);
  const busy = deploy.isPending || deployRepo.isPending || savingConn || starting;
  const actionsActive = Boolean(actionsRun && actionsRun.state !== "completed");

  async function checkWebsiteAfterDeploy() {
    try {
      const result = await api.post(`/domains/${domain.id}/deploy/check`);
      await queryClient.invalidateQueries({ queryKey: ["domains"] });
      return result.isUp ? "Up" : "Down";
    } catch {
      return "Not checked";
    }
  }

  // Reset per domain, and take the server's suggested folder once it loads.
  useEffect(() => {
    setZipFile(null);
    setProgress("");
    setConnEdited(false);
    setActionsRun(null);
  }, [domain?.id]);

  // Follows a GitHub Actions deploy until it finishes. The run takes a few
  // seconds to appear on GitHub, then waits for the runner, then uploads.
  useEffect(() => {
    if (!actionsRun?.requestId || actionsRun.state === "completed" || !domain?.id) return undefined;
    const { requestId } = actionsRun;
    const timer = setInterval(async () => {
      try {
        const r = await api.get(`/domains/${domain.id}/deploy-actions/${requestId}`);
        setActionsRun((cur) => (cur?.requestId === requestId ? { ...cur, ...r } : cur));
        if (r.state === "completed") {
          if (r.conclusion === "success") {
            const websiteStatus = await checkWebsiteAfterDeploy();
            setActionsRun((cur) =>
              cur?.requestId === requestId ? { ...cur, websiteStatus } : cur
            );
            toast.success(`${domain.name} deployed via GitHub Actions · Website ${websiteStatus}`);
          } else {
            toast.error(`Deploy ${r.conclusion || "failed"} — open the GitHub log to see why`);
          }
        }
      } catch {
        /* status is best-effort — keep following */
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [actionsRun?.requestId, actionsRun?.state, domain?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (info.data?.path) setPath(info.data.path);
  }, [info.data?.path]);

  // The POST stays open for the whole upload, so progress comes from polling.
  useEffect(() => {
    if (!busy || !domain?.id) return undefined;
    const timer = setInterval(async () => {
      try {
        const p = await api.get(`/domains/${domain.id}/github-upload/status`);
        if (p.phase === "extracting") setProgress("Extracting on the server…");
        else if (p.bytes) setProgress(`Uploading… ${(p.bytes / 1048576).toFixed(0)} MB sent`);
      } catch {
        /* progress is best-effort */
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [busy, domain?.id]);

  const loadError = info.isError ? info.error?.message : "";
  const hasRepo = Boolean(info.data?.githubRepoUrl);
  const viaFtp = info.data?.deployMethod === "ftp";
  // Whichever credentials this server's method needs.
  const credentialsReady = viaFtp ? info.data?.ftpConfigured : info.data?.sshConfigured;
  // With a repo there is nothing to choose; without one, a ZIP is required.
  // Missing credentials are fine as long as the form below has enough to save.
  const connUsable = Boolean(conn?.host && conn?.username && conn?.password);
  const ready = (credentialsReady || connUsable) && path && (hasRepo || Boolean(zipFile));

  useEffect(() => {
    if (info.data) setSource(hasRepo ? "repo" : "zip");
  }, [info.data, hasRepo]);

  // Prefill the connection form from whatever the server already has.
  useEffect(() => {
    if (!info.data) return;
    setConn(connFromServer(info.data));
  }, [info.data]);

  // Writes the typed details, then the deploy uses them.
  //
  // FTP details go on the DOMAIN: each site has its own FTP account, locked to
  // its own folder, and saving them on the shared server would point every
  // other domain on it at this one's folder. The API also copies them into the
  // repo's secrets so a push to GitHub deploys the same way.
  //
  // SSH stays on the server — one login reaches every site on the machine. That
  // record is fetched and merged first, because the API replaces the whole row
  // and a partial save would wipe the other fields.
  async function saveConnection() {
    if (conn.method === "ftp") {
      await api.put(`/domains/${domain.id}/ftp`, {
        ftpHost: conn.host,
        ftpPort: conn.port ? Number(conn.port) : 21,
        ftpUsername: conn.username,
        ftpRoot: conn.ftpRoot,
        ...(conn.password ? { ftpPassword: conn.password } : {}),
      });
      await info.refetch();
      return;
    }

    const id = info.data?.serverId;
    if (!id) throw new Error("This domain has no server linked.");
    const current = await api.get(`/servers/${id}`);
    const merged = { ...current, ...connToServerPayload(conn) };
    // Never echo secrets that came back masked.
    delete merged.sshKeySaved;
    delete merged.ftpPasswordSaved;
    delete merged.panelApiKeySaved;
    await api.put(`/servers/${id}`, merged);
    await info.refetch();
  }

  function pickZip(event) {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;
    const cap = githubStatus?.hardLimitMb || githubStatus?.zipLimitMb;
    if (cap && file.size > cap * 1048576) {
      toast.error(`"${file.name}" is ${(file.size / 1048576).toFixed(0)} MB. The limit is ${cap} MB.`);
      return;
    }
    setZipFile(file);
  }

  async function handleDeploy() {
    try {
      if (!credentialsReady || connEdited) {
        setSavingConn(true);
        setProgress("Saving connection details…");
        try {
          await saveConnection();
        } finally {
          setSavingConn(false);
        }
      }
      // GitHub Actions: ask GitHub to start the repo's workflow; the runner
      // inside the network does the upload. Progress is followed above.
      if (info.data?.deployViaActions) {
        setStarting(true);
        setProgress("Asking GitHub to start the deploy…");
        try {
          const { requestId } = await api.post(`/domains/${domain.id}/deploy-actions`, {});
          setActionsRun({ requestId, state: "starting", since: Date.now() });
        } finally {
          setStarting(false);
        }
        return;
      }

      setProgress(source === "repo" ? "Fetching code from GitHub…" : "Connecting…");
      const res =
        source === "repo"
          ? await deployRepo.mutateAsync({ id: domain.id, path })
          : await deploy.mutateAsync({ id: domain.id, file: zipFile, path });
      const websiteStatus = await checkWebsiteAfterDeploy();
      // `files` is null when the panel unzipped one archive server-side.
      toast.success(
        `${res.files != null ? `Deployed ${res.files} files to ${res.path}` : `Deployed to ${res.path}`} · Website ${websiteStatus}`
      );
      onClose?.();
    } catch (err) {
      toast.error(err.message || "Deploy failed");
    } finally {
      setProgress("");
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: HEADING_FONT, fontWeight: 700, display: "flex", gap: 1.5, alignItems: "center" }}>
        <RocketLaunchOutlinedIcon sx={{ color: PURPLE }} />
        Deploy to server
      </DialogTitle>

      <DialogContent dividers>
        {info.isLoading && (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={22} />
          </Stack>
        )}

        {loadError && <Alert severity="error">{loadError}</Alert>}

        {info.data && (
          <>
            <Box sx={{ mb: 2 }}>
              <ServerConnectionForm
                key={domain?.id}
                value={conn}
                onChange={markEdited}
                // FTP accounts are per site, so the heading names the site.
                serverName={conn?.method === "ftp" ? domain?.name : info.data.serverName}
                configured={credentialsReady}
                busy={busy}
                note={
                  conn?.method === "ftp"
                    ? `${connEdited ? "Saved" : "Stored"} on ${domain?.name || "this domain"} only (encrypted), and copied into its GitHub repo's secrets for automatic deploys.`
                    : connEdited
                      ? `Saved on ${info.data.serverName} (encrypted) when you deploy.`
                      : `Stored on ${info.data.serverName} (encrypted) and shared by every domain on it.`
                }
              />
            </Box>

            {/* Over FTP the destination is the account's own root — nothing to
                choose, and nothing that could send the files astray. It is
                shown just above, on the connection line. */}
            {!viaFtp && (
              <>
                <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, color: MUTED }}>
                  Target folder on the server
                </Typography>
                <TextField
                  value={path}
                  onChange={(e) => setPath(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 0.5, mb: 2 }}
                  disabled={busy}
                />
              </>
            )}

            {/* Only when there is no repo to take the code from. */}
            {!hasRepo && (
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minHeight: 52,
                  px: 2,
                  borderRadius: 2,
                  border: `1px dashed ${zipFile ? BLUE : HAIRLINE}`,
                  background: TILE,
                  cursor: busy ? "default" : "pointer",
                }}
              >
                <FolderZipOutlinedIcon sx={{ fontSize: 20, color: zipFile ? BLUE : MUTED }} />
                <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: zipFile ? "#0F172A" : MUTED }}>
                  {zipFile
                    ? `${zipFile.name} (${(zipFile.size / 1048576).toFixed(1)} MB)`
                    : "Choose the ZIP to deploy"}
                </Typography>
                <input type="file" accept=".zip,application/zip" hidden disabled={busy} onChange={pickZip} />
              </Box>
            )}

            <Alert severity="warning" sx={{ mt: 2 }}>
              This changes the live website. Files in the ZIP overwrite the ones on the server;
              everything else (uploads, media) is left alone.{" "}
              {info.data.protectedFiles?.length > 0 && (
                <>Never overwritten: {info.data.protectedFiles.join(", ")}.</>
              )}
            </Alert>

            {actionsRun ? (
              <ActionsRunStatus run={actionsRun} />
            ) : (
              progress && (
                <Typography sx={{ mt: 2, fontSize: 13, fontWeight: 600, color: PURPLE }}>
                  {progress}
                </Typography>
              )
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* A GitHub Actions deploy carries on without this dialog. */}
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 600, color: MUTED }}>
          {actionsRun ? "Close" : "Cancel"}
        </Button>
        <Button
          onClick={handleDeploy}
          disabled={!ready || busy || actionsActive}
          variant="contained"
          startIcon={
            busy || actionsActive ? (
              <CircularProgress size={15} sx={{ color: "#fff" }} />
            ) : (
              <RocketLaunchOutlinedIcon />
            )
          }
          sx={{ textTransform: "none", fontWeight: 700, background: PURPLE, "&:hover": { background: "#6D28D9" } }}
        >
          {busy || actionsActive ? "Deploying…" : actionsRun ? "Deploy again" : "Deploy"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Where a GitHub Actions deploy is: starting → waiting for the runner →
// uploading → done, with the run's log one click away.
function ActionsRunStatus({ run }) {
  const done = run.state === "completed";
  const ok = done && run.conclusion === "success";
  // A run still queued after a minute almost always means no runner is online.
  const stuck =
    (run.state === "queued" || run.state === "waiting") && Date.now() - (run.since || 0) > 60000;

  const text = ok
    ? `Deployed — the runner uploaded the repo over FTP. Website: ${run.websiteStatus || "checking…"}`
    : done
      ? `Deploy ${run.conclusion || "failed"}. Open the log to see why.`
      : run.state === "in_progress"
        ? "Deploying — the runner is uploading over FTP…"
        : run.state === "starting"
          ? "Starting on GitHub…"
          : stuck
            ? "Still waiting for the runner. Is the deploy VM switched on?"
            : "Waiting for the runner to pick it up…";

  return (
    <Alert
      severity={ok ? "success" : done ? "error" : stuck ? "warning" : "info"}
      icon={done ? undefined : <CircularProgress size={18} />}
      sx={{ mt: 2 }}
      action={
        run.url && (
          <Button
            href={run.url}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            View log
          </Button>
        )
      }
    >
      {text}
    </Alert>
  );
}

export default DeployDialog;
